import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Send, X } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useWhatsAppTemplates } from '@/hooks/useWhatsAppTemplates';
import { getTemplateParameters, type TemplateParameter } from '@/lib/whatsapp-template-parser';
import { getErrorMessage } from '@/lib/error-utils';
import { readTemplateSendRefusal } from '@/lib/template-send-refusal';

interface WhatsAppTemplateSelectorProps {
  /** The conversation this template belongs to. Without it the edge function refuses:
   *  a template sent from a conversation the platform marked as a rehearsal used to leave
   *  through the tenant's real commercial number. */
  conversationId?: string | null;
  customerPhone?: string;
  customerName?: string;
  onTemplateSent?: () => void;
  onCancel?: () => void;
}

export function WhatsAppTemplateSelector({
  conversationId,
  customerPhone,
  customerName,
  onTemplateSent,
  onCancel
}: WhatsAppTemplateSelectorProps) {
  const { toast } = useToast();
  const { t } = useTranslation('conversations');
  const { data: templates = [], isLoading: loading } = useWhatsAppTemplates({ statusFilter: 'approved' });
  const [selectedTemplate, setSelectedTemplate] = useState<string>('');
  const [parameters, setParameters] = useState<string[]>([]);
  const [templateParams, setTemplateParams] = useState<TemplateParameter[]>([]);
  const [sending, setSending] = useState(false);

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
    const template = templates.find(t => t.id === templateId);

    // Use shared parser for ALL component parameters (header, body, buttons)
    if (template?.components) {
      const params = getTemplateParameters(template.components);
      setTemplateParams(params);
      const initialParams = new Array(params.length).fill('');

      // Auto-populate first BODY parameter with customer name if available
      if (customerName) {
        const firstBodyIdx = params.findIndex(p => p.component === 'BODY');
        if (firstBodyIdx >= 0) {
          initialParams[firstBodyIdx] = customerName;
        }
      }

      setParameters(initialParams);
    } else {
      setTemplateParams([]);
      setParameters([]);
    }
  };

  const handleSendTemplate = async () => {
    if (!selectedTemplate || !customerPhone) {
      toast({
        title: t('common:error'),
        description: t('templateSelector.toasts.selectTemplateRequired'),
        variant: "destructive"
      });
      return;
    }

    const template = templates.find(t => t.id === selectedTemplate);
    if (!template) return;

    try {
      setSending(true);

      const { data, error } = await supabase.functions.invoke('whatsapp-send-template', {
        body: {
          conversationId,
          recipientPhone: customerPhone,
          templateName: template.template_name,
          templateLanguage: template.language,
          templateParameters: parameters.filter(p => p.trim()),
          templateDefinition: template.components
        }
      });

      if (error) throw error;

      toast({
        title: t('common:success'),
        description: t('templateSelector.toasts.sendSuccess'),
      });

      onTemplateSent?.();
    } catch (error: unknown) {
      if (import.meta.env.DEV) console.error('Error sending template:', error);
      // A refusal carries a code and a reason; without this the operator reads the platform
      // client's transport string and learns nothing.
      const refusal = await readTemplateSendRefusal(error);
      toast({
        // A refusal is not a failure: the send was stopped on purpose, and a title that says
        // «failed» makes it look like the network broke. Judged 2026-09-08.
        title: refusal ? t('template.refusedTitle') : t('common:error'),
        description: refusal
          ? t(refusal.messageKey)
          : (getErrorMessage(error) || t('templateSelector.toasts.sendFailed')),
        variant: "destructive"
      });
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-center">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            {t('templateSelector.loading')}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (templates.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">{t('templateSelector.emptyTitle')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            {t('templateSelector.emptyDescription')}
          </p>
          {/* N-15: this used to explain the gap and leave the operator to find the door
              themselves. It links straight to where a template gets created and approved. */}
          <div className="flex gap-2">
            <Button size="sm" asChild>
              <a href="/tenant-admin/templates/whatsapp">{t('templateSelector.emptyAction')}</a>
            </Button>
            <Button variant="outline" size="sm" onClick={onCancel}>
              {t('common:cancel')}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Helper function: Replace {{n}} or {{name}} placeholders with parameter values for a specific component
  const replaceComponentPlaceholders = (text: string, component: 'HEADER' | 'BODY'): string => {
    let result = text;
    templateParams.forEach((param, flatIdx) => {
      if (param.component === component) {
        // Named params use param.name (e.g. "nombre"), numbered use index (e.g. "1")
        const isNamed = !/^(body|header)_\d+$/.test(param.name);
        const placeholder = isNamed ? `{{${param.name}}}` : `{{${param.index}}}`;
        const escapedPlaceholder = placeholder.replace(/[{}]/g, '\\$&');
        result = result.replace(
          new RegExp(escapedPlaceholder, 'g'),
          parameters[flatIdx] || placeholder
        );
      }
    });
    return result;
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm">{t('templateSelector.title')}</CardTitle>
          <Button variant="ghost" size="sm" onClick={onCancel}>
            <X className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label htmlFor="template">{t('templateSelector.selectLabel')}</Label>
          <Select value={selectedTemplate} onValueChange={handleTemplateSelect}>
            <SelectTrigger>
              <SelectValue placeholder={t('templateSelector.selectPlaceholder')} />
            </SelectTrigger>
            <SelectContent>
              {templates.map((template) => (
                <SelectItem key={template.id} value={template.id}>
                  {template.template_name} ({template.category})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Template Preview - Live updating */}
        {selectedTemplate && (() => {
          const template = templates.find(t => t.id === selectedTemplate);
          const headerComponent = template?.components?.find((c: { type: string; text?: string }) => c.type === 'HEADER');
          const bodyComponent = template?.components?.find((c: { type: string; text?: string }) => c.type === 'BODY');

          return template ? (
            <div className="bg-muted/50 p-4 rounded-lg space-y-2">
              <Label className="text-xs font-semibold">{t('templateSelector.previewLabel')}</Label>
              <div className="bg-card border rounded-md p-3 space-y-2 text-sm">
                {headerComponent?.text && (
                  <div className="font-semibold text-foreground">
                    {replaceComponentPlaceholders(headerComponent.text, 'HEADER')}
                  </div>
                )}
                {bodyComponent?.text && (
                  <div className="text-foreground whitespace-pre-wrap">
                    {replaceComponentPlaceholders(bodyComponent.text, 'BODY')}
                  </div>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                {t('templateSelector.categoryLanguage', { category: template.category, language: template.language })}
              </p>
            </div>
          ) : null;
        })()}

        {/* Template Parameters */}
        {parameters.length > 0 && (
          <div className="space-y-2">
            <Label>{t('templateSelector.parametersLabel')}</Label>
            {parameters.map((param, index) => (
              <Input
                key={index}
                placeholder={templateParams[index]?.label || t('templateSelector.parameterFallback', { index: index + 1 })}
                value={param}
                onChange={(e) => {
                  const newParams = [...parameters];
                  newParams[index] = e.target.value;
                  setParameters(newParams);
                }}
              />
            ))}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <Button variant="outline" size="sm" onClick={onCancel}>
            {t('common:cancel')}
          </Button>
          <Button 
            size="sm" 
            onClick={handleSendTemplate}
            disabled={!selectedTemplate || sending}
          >
            {sending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Send className="h-4 w-4 mr-2" />
            )}
            {t('sendTemplate')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}